/**
 * Workflow Help — explains the e-room design steps and clarifies common confusion
 * (Equipment library vs Equipment list, where trays go, etc.). Plain JSX modal.
 */
import { X, MapTrifold, Stack, Cube, Path, Wrench } from "@phosphor-icons/react";

const SECTIONS = [
  {
    icon: MapTrifold,
    title: "1 — Set room dimensions",
    body: (
      <>
        On the <b className="text-[#00E5FF]">Size</b> tab, enter the e-room <b>length × width × depth</b> (depth = ceiling height,
        must be ≥ 78&quot; per NEC 110.26(E)). These dimensions define the 3D shell and
        the working-space rules everything below relies on.
      </>
    ),
  },
  {
    icon: Stack,
    title: "2 — Add equipment to the EQUIPMENT LIST (not the catalog library)",
    body: (
      <>
        On the <b className="text-[#00E5FF]">Equipment</b> tab → <b>Equipment list</b> section, click
        <b className="text-[#00E5FF]"> Add equipment-list item</b> or import a CSV. Each row needs
        a tag + physical dimensions (length × width × depth). These rows render in 3D.
        <br /><br />
        <span className="text-[#FFCC00]">⚠ The <i>Library / catalog</i> section is for BOM parts only</span>
        — adding a Square&nbsp;D breaker there will NOT add a box to the 3D view.
        For physical equipment, use the equipment list.
      </>
    ),
  },
  {
    icon: Cube,
    title: "3 — Place equipment (or use Auto-layout)",
    body: (
      <>
        Either drag each item on the 2D <b>Plan View</b> (Size tab) or click
        <b className="text-[#00E5FF]"> Auto-layout</b> — it places items along the back wall
        of the room respecting NEC 110.26(A) working space. Auto-layout never deletes items;
        if it cannot fit one, the item stays in the list with
        <span className="text-[#FFCC00]"> position: null</span> and is parked in the staging
        rail to the LEFT of the room in 3D.
      </>
    ),
  },
  {
    icon: Path,
    title: "4 — Add cable trays + conduits",
    body: (
      <>
        Open the <b className="text-[#00E5FF]">Cables</b> tab → <b>Tray Designer</b>:
        <ol className="list-decimal ml-5 my-2 space-y-1 text-gray-300">
          <li>Pick a <b>manufacturer</b> (ABB Cope · Eaton B-Line · Legrand Cablofil · Atkore Unistrut).</li>
          <li>Set <b>tray type / width / depth / material</b> — defaults flow to every new segment.</li>
          <li>Click <b>Auto-route</b> or pick a <b>1-click template</b> (back-wall run / perimeter / H-pattern / MCC + main feeders / two-tier).</li>
          <li>On the 2D plan, <b>drag the green handles</b> to fine-tune endpoints. Click anywhere on a segment to insert a <b>fitting</b> (T / cross / 90° elbow).</li>
          <li>Watch the live <b>NEC 392.22 fill</b> badge — colors track fill %: green ≤30, amber 30-50, red &gt;50.</li>
          <li>Optionally enter a <b>Distributor Multiplier</b> (e.g. 0.55 = 45% off list) to get net pricing in the BOM rollup + PDF.</li>
        </ol>
        Cable trays draw at <b>ceiling-12&quot;</b> in 3D; drops extend down to the top of equipment (~78&quot;).
        Click a rail in 3D to <b>explode</b> it and reveal the cables inside.
      </>
    ),
  },
  {
    icon: Wrench,
    title: "5 — Validate &amp; iterate",
    body: (
      <>
        On the <b className="text-[#00E5FF]">3D View</b>:
        <ul className="list-disc ml-5 my-2 space-y-1 text-gray-300">
          <li><b>Auto-validate</b> (on by default) re-runs NEC 110.26 checks after every drag.</li>
          <li><b>Lock view</b> freezes the camera so you can position equipment without the scene swinging.</li>
          <li><b>DIM</b> toggle shows color-coded distance lines to each wall (N=cyan, S=amber, E=magenta, W=lime) and the roof (violet). Numbers also appear under the selected item in the side-panel and are editable — type a new clearance to snap-position the box.</li>
          <li><b>Snap-to-wall</b> is automatic: drag an item within 6&quot; of any wall and it locks flush.</li>
          <li><b>AI Audit</b> calls Claude Sonnet 4.5 for a senior-engineer plan review with NEC/CEC citations.</li>
        </ul>
        Export DXF / DWG / PDF from the project header when you&apos;re happy.
      </>
    ),
  },
];

const FAQ = [
  {
    q: "Auto-layout removed my equipment.",
    a: "Auto-layout only operates on Equipment-list items (Equipment tab → Equipment list). Items added on the catalog library section are BOM parts and never appear in 3D. If your equipment list was empty when you clicked Auto-layout, you'll see a warning saying so.",
  },
  {
    q: "Why is my tray drawn outside the room in 3D?",
    a: "Tray points are clamped to the room footprint when you draw them. Trays mount at ceiling-12\" by default. If a tray seems off-room, check that the room depth (ceiling height) is set on the Size tab.",
  },
  {
    q: "What's the difference between Equipment tab and Equipment list?",
    a: "Equipment tab = catalog parts for the BOM (breakers, contactors, lugs). Equipment list = physical objects with dimensions that occupy floor space (MCC, transformer, panelboard). 3D renders only the equipment list.",
  },
  {
    q: "Can I lock the camera so I can adjust equipment without it moving?",
    a: "Yes — click the lock icon on the 3D toolbar to disable orbit/zoom/pan. Re-click to unlock.",
  },
  {
    q: "Cu or Al for the cable schedule?",
    a: "Cables tab → Conductor material dropdown. Switching re-runs sizing against NEC 310.16 or CEC Table 2 aluminum columns (≈22% larger gauge for the same load, ~60% higher resistance ⇒ Vd grows).",
  },
  {
    q: "How do I edit the dimension that appears under an equipment in 3D View?",
    a: "Turn on DIM mode in the 3D toolbar, click an equipment to select it, then the side-panel row expands with N / S / E / W / roof distances in their matching overlay colors. Type any new value and the equipment snaps to that clearance.",
  },
  {
    q: "I'm filling the Equipment List CSV from scratch — where are the instructions?",
    a: "Equipment tab → Equipment list → Template button downloads a blank CSV pre-loaded with instruction lines (units, tag format, blank vs zero). Strip every line starting with '#' before re-importing.",
  },
];

export function HelpContent() {
  return (
    <>
      {SECTIONS.map((s) => {
        const Icon = s.icon;
        return (
          <div key={s.title} className="border-l-2 border-[#00E5FF]/50 pl-4 mb-5">
            <div className="flex items-center gap-2 mb-1 text-[#00E5FF] font-display text-sm uppercase tracking-widest">
              <Icon size={18} weight="duotone" />
              {s.title}
            </div>
            <div className="text-sm text-gray-300 leading-relaxed">{s.body}</div>
          </div>
        );
      })}

      <div className="border border-white/10 p-4 mt-6">
        <div className="label-tag mb-3">// FAQ</div>
        <dl className="space-y-3 text-sm">
          {FAQ.map((f) => (
            <div key={f.q}>
              <dt className="text-[#FFCC00] font-mono text-xs">Q: {f.q}</dt>
              <dd className="text-gray-300 mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </div>
    </>
  );
}

export default function HelpModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
         onClick={onClose} data-testid="r3d-help-modal">
      <div className="bg-[#0a0d12] border border-[#00E5FF]/30 w-full max-w-3xl max-h-[88vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <div className="label-tag">// Help · workflow</div>
            <h3 className="font-display text-lg font-bold">Designing an electrical room — step by step</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="help-close">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-auto p-5 flex-1" data-testid="help-body">
          <HelpContent />
        </div>
      </div>
    </div>
  );
}
